import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Radio } from "lucide-react";
import { api, faNum } from "../lib/api";

// افزودن و ویرایش پروایدر از روی فایل اسکریپت (پوشه‌ی app/provider_adapters روی سرور).
// پنل فایل‌ها را فقط فهرست و تست می‌کند؛ هیچ کدی آپلود یا ویرایش نمی‌شود.

const SAMPLE_OK = (kind) => kind === "matched" || kind === "not_matched";

function errorText(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ؛ ");
  return `خطای سرور (${err?.response?.status || "نامشخص"})`;
}

export default function ProviderScriptDialog({ open, mode, item, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [priority, setPriority] = useState(10);
  const [timeoutMs, setTimeoutMs] = useState(5000);
  const [script, setScript] = useState("");
  const [scripts, setScripts] = useState([]);
  const [sample, setSample] = useState({ national_code: "", mobile: "" });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testedOk, setTestedOk] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(item?.name ?? "");
    setPriority(item?.priority ?? 10);
    setTimeoutMs(item?.timeout_ms ?? 5000);
    setScript(item?.adapter ?? "");
    setSample({ national_code: item?.sample_national_code ?? "", mobile: item?.sample_mobile ?? "" });
    setTestResult(null);
    setTestedOk("");
    api.get("/admin/provider-scripts")
      .then(({ data }) => setScripts(data.data.items || []))
      .catch((e) => toast.error(errorText(e)));
  }, [open, item]);

  if (!open) return null;

  // اگر اسکریپت تغییر کرده یا پروایدر جدید است، تست موفق لازم است
  const needsTest = !!script && (mode !== "edit" || script !== (item?.adapter ?? ""));
  const saveBlocked = needsTest && testedOk !== script;

  const runTest = async () => {
    if (!script) return toast.error("ابتدا یک فایل اسکریپت انتخاب کنید");
    if (!/^\d{10}$/.test(sample.national_code)) return toast.error("کدملی نمونه باید ۱۰ رقم باشد");
    if (!/^\d{10,11}$/.test(sample.mobile)) return toast.error("موبایل نمونه باید ۱۰ یا ۱۱ رقم باشد");
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/provider-scripts/test", {
        name: script, national_code: sample.national_code, mobile: sample.mobile,
        timeout_ms: Number(timeoutMs) || 5000,
      });
      setTestResult(data.data);
      setTestedOk(SAMPLE_OK(data.data.kind) ? script : "");
    } catch (e) {
      toast.error(errorText(e));
    }
    setTesting(false);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("نام پروایدر را وارد کنید");
    if (!script) return toast.error("فایل اسکریپت را انتخاب کنید");
    if (saveBlocked) return toast.error("ابتدا تست اسکریپت باید موفق باشد");
    const body = {
      name: name.trim(), adapter: script,
      priority: Number(priority), timeout_ms: Number(timeoutMs),
      enabled: item ? item.enabled : true,
    };
    setSaving(true);
    try {
      if (mode === "edit") await api.put(`/admin/providers/${item.id}/script`, body);
      else await api.post("/admin/providers/script", body);
      toast.success(mode === "edit" ? "تغییرات ذخیره شد" : "پروایدر اضافه شد");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(errorText(e));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{mode === "edit" ? "ویرایش پروایدر" : "افزودن پروایدر"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="بستن"><X size={18} /></button>
        </div>

        <section className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-3">
            <label className="text-xs text-slate-600 mb-1 block">نام پروایدر</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">اولویت (عدد کمتر = بالاتر)</label>
            <input type="number" value={priority} onChange={(e) => setPriority(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">تایم‌اوت (میلی‌ثانیه)</label>
            <input type="number" value={timeoutMs} onChange={(e) => setTimeoutMs(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" />
          </div>
        </section>

        <section className="space-y-3 rounded-2xl border border-slate-200 p-4">
          <div className="font-medium text-sm text-slate-700">فایل اسکریپت پروایدر</div>
          <select value={script}
            onChange={(e) => { setScript(e.target.value); setTestResult(null); setTestedOk(""); }}
            className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm">
            <option value="">— انتخاب کنید —</option>
            {scripts.map((s) => (
              <option key={s.name} value={s.name} disabled={!s.valid}>
                {s.name}{s.description ? ` — ${s.description}` : ""}{!s.valid ? ` (${s.error})` : ""}
              </option>
            ))}
          </select>
          {scripts.length === 0 && (
            <p className="text-xs text-slate-500">هیچ فایلی در پوشه‌ی app/provider_adapters پیدا نشد.</p>
          )}

          {script && (
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600 mb-1 block">کدملی نمونه</label>
                  <input dir="ltr" value={sample.national_code}
                    onChange={(e) => setSample((s) => ({ ...s, national_code: e.target.value.trim() }))}
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-600 mb-1 block">موبایل نمونه</label>
                  <input dir="ltr" value={sample.mobile}
                    onChange={(e) => setSample((s) => ({ ...s, mobile: e.target.value.trim() }))}
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" />
                </div>
              </div>
              <button onClick={runTest} disabled={testing}
                className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm flex items-center gap-2 disabled:opacity-60">
                {testing ? <Loader2 size={15} className="animate-spin" /> : <Radio size={15} />} تست اسکریپت
              </button>
              {testResult && (
                <div className="rounded-xl border border-slate-200 p-3 space-y-2 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs border ${
                      testResult.kind === "matched" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : testResult.kind === "not_matched" ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                      {testResult.kind === "matched" ? "تطابق" : testResult.kind === "not_matched" ? "عدم تطابق" : testResult.kind === "timeout" ? "تایم‌اوت" : "خطا"}
                    </span>
                    <span className="text-xs text-slate-500">{faNum(testResult.latency_ms)} ms</span>
                  </div>
                  {testResult.error && <div className="text-xs text-rose-600">{testResult.error}</div>}
                </div>
              )}
              {saveBlocked && <p className="text-xs text-amber-700">برای ذخیره، ابتدا تست اسکریپت باید موفق باشد.</p>}
            </div>
          )}
        </section>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-300 text-sm">انصراف</button>
          <button onClick={save} disabled={saving || saveBlocked}
            className="px-5 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm flex items-center gap-2 disabled:opacity-50">
            {saving && <Loader2 size={15} className="animate-spin" />} ذخیره
          </button>
        </div>
      </div>
    </div>
  );
}
